/**
 * Modal for creating/editing help quick actions
 */

import { useState, useEffect } from 'react';
import { X, Zap, Save } from 'lucide-react';

interface QuickActionFormData {
  id?: string;
  title: string;
  description: string;
  icon: string;
  buttonText: string;
  linkUrl: string;
  order: number;
  published: boolean;
}

interface QuickActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: QuickActionFormData, isEdit: boolean) => Promise<void>;
  editingQuickAction: QuickActionFormData | null;
}

export default function QuickActionModal({
  isOpen,
  onClose,
  onSave,
  editingQuickAction,
}: QuickActionModalProps): JSX.Element | null {
  const [formData, setFormData] = useState<QuickActionFormData>({
    title: '',
    description: '',
    icon: 'Zap',
    buttonText: 'Learn More',
    linkUrl: '',
    order: 0,
    published: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens/closes or editing quick action changes
  useEffect(() => {
    if (isOpen) {
      if (editingQuickAction) {
        setFormData(editingQuickAction);
      } else {
        setFormData({
          title: '',
          description: '',
          icon: 'Zap',
          buttonText: 'Learn More',
          linkUrl: '',
          order: 0,
          published: false,
        });
      }
      setError(null);
    }
  }, [isOpen, editingQuickAction]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!formData.description.trim()) {
      setError('Description is required');
      return;
    }
    if (!formData.icon.trim()) {
      setError('Icon is required');
      return;
    }
    if (!formData.buttonText.trim()) {
      setError('Button text is required');
      return;
    }
    if (!formData.linkUrl.trim()) {
      setError('Link URL is required');
      return;
    }

    // Validate URL format
    try {
      // Allow relative URLs starting with / or absolute URLs with protocol
      if (!formData.linkUrl.startsWith('/') && !formData.linkUrl.match(/^https?:\/\//)) {
        setError('Link URL must start with / (internal) or http:// / https:// (external)');
        return;
      }
    } catch {
      setError('Invalid URL format');
      return;
    }

    try {
      setSaving(true);
      await onSave(formData, !!editingQuickAction);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save quick action');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <Zap size={24} className="text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {editingQuickAction ? 'Edit Quick Action' : 'Create Quick Action'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {editingQuickAction ? 'Update quick action details' : 'Add a new quick action'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="input w-full"
              placeholder="e.g., Browse Help Articles"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input w-full"
              rows={3}
              placeholder="Brief description of what this action does"
              required
            />
          </div>

          {/* Icon */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Icon <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              className="input w-full"
              placeholder="e.g., Zap, Book, MessageCircle"
              required
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Lucide icon name (see{' '}
              <a
                href="https://lucide.dev/icons"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                lucide.dev
              </a>
              )
            </p>
          </div>

          {/* Button Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Button Text <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.buttonText}
              onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })}
              className="input w-full"
              placeholder="e.g., Browse Articles, Contact Support"
              required
            />
          </div>

          {/* Link URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Link URL <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.linkUrl}
              onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
              className="input w-full"
              placeholder="/help or https://example.com"
              required
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Internal links start with / (e.g., /help, /contact). External links include full URL.
            </p>
          </div>

          {/* Order */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Display Order
            </label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              className="input w-full"
              min="0"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Lower numbers appear first
            </p>
          </div>

          {/* Published */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.published}
                onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Publish immediately
              </span>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
              Unpublished quick actions won't appear on the help center
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary inline-flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  {editingQuickAction ? 'Update' : 'Create'} Quick Action
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
